import '../../../../src/environment.d.ts';
import { environment } from '../../../environments/environment';
import { Helper } from '../../Common/Helpers/helper';
import { BackendApiResponse, HttpClientOptionsBase } from '../../Classes/visibaHttp.class';
import { Injectable, NgZone } from '@angular/core';
import { forkJoin } from 'rxjs';
import { UnitConfigApiModel } from '../api/v2/Models/UnitConfigApiModel';
import { UnitAboutApiModel } from '../api/v2/Models/UnitAboutApiModel';
import { IdentityApiModel } from '../api/v2/Models/IdentityApiModel';
import { ResourceIdentityApiModel } from '../api/v2/Models/ResourceIdentityApiModel';
import { UnitModel } from '../api/v2/Models/UnitModel';
import { HttpClient } from '@angular/common/http';
import { ResolveStart, Router } from '@angular/router';
import { root } from '../../routes';
import { UnitService } from '@services/unit.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { EnvironmentService } from '@services/environment.service/mod';
import { StartupService as InjectedStartupService } from '@services/startup.service';
import { LocationSelectorService } from '@services/location_selector.service';
import { UserSettingsService } from '@services/user_settings.service';
import { ConfigService } from '@services/config.service';
import { sharedResolution } from '../../../../src/shared_resolution';
import { ShellController } from '@application/shell/shell.controller';
import type { UnitConfigApiModel as NewUnitConfigApiModel } from '@api/generated/models';
import { UniversalConfig } from '@application/universal_config.service';
import { UnitService as UnitService_from_react_only } from '@application/unit.service';
import { authServiceThatOriginatesFromReactOnly } from '../../../../src/application/services/auth/temp_migration_file';
import { AuthService } from '@application/authentication/auth.service';

@Injectable()
export class StartupService {
	webVersion: string = null;
	unitConfigData: UnitConfigApiModel = null;
	unitAboutData: UnitAboutApiModel = null;
	unitTreeData: UnitModel = null;
	patientIdentityData: IdentityApiModel = null;
	resourceIdentityData: ResourceIdentityApiModel = null;

	private readonly environmentService = sharedResolution.resolve(EnvironmentService);
	private readonly injectedStartupService = sharedResolution.resolve(InjectedStartupService);
	private readonly unitService = sharedResolution.resolve(UnitService);
	private readonly licenceHolderService = sharedResolution.resolve(LicenceHolderService);
	private readonly locationSelectorService = sharedResolution.resolve(LocationSelectorService);
	private readonly userSettingsService = sharedResolution.resolve(UserSettingsService);
	private readonly configService = sharedResolution.resolve(ConfigService);

	constructor(
		private _http: HttpClient,
		private helper: Helper,
		private readonly router: Router,
		private readonly ngZone: NgZone,
	) {}

	// This is the method you want to call at bootstrap
	// Important: It should return a Promise
	async load(): Promise<any> {
		authServiceThatOriginatesFromReactOnly.instance = sharedResolution.resolve(AuthService);

		this.useGoogleFontPrevention();

		window.__TEMP__ = true;
		window.__TEMP__overrideNavigation = (to: string) => {
			this.ngZone.run(() => {
				this.router.navigateByUrl(to);
			});
		};

		this.environmentService.hosting.apiBaseUri = environment.api.uri + '/api';

		const shell = sharedResolution.resolve(ShellController);
		const unitService = sharedResolution.resolve(UnitService_from_react_only);
		const universalConfig = sharedResolution.resolve(UniversalConfig);
		const result = await shell.boot();
		if (result == null) {
			throw new Error('Shell failed to boot');
		}

		await this.injectedStartupService.boot(result);

		const userSettings = this.userSettingsService.getSettings();
		if (localStorage.getItem('languageCode') == null) {
			localStorage.setItem('languageCode', userSettings.selectedLanguage);
		}

		this.createRoutes();

		this.router.events.subscribe((event) => {
			if (event instanceof ResolveStart) {
				const traceableRoute = this.unitService.normalizePathnameToBackTraceableRoute(event.url);

				this.locationSelectorService.commitByRoute(traceableRoute);

				const isReception = unitService.normalizePathnameToBackTraceableRoute(window.location.pathname);
				const unitId = unitService.backTraceUnitIdFromRoute(isReception);
				universalConfig.currentUnitId = unitId;
			}
		});

		return new Promise<void>(async (resolve, reject) => {
			const requestOptions: HttpClientOptionsBase = {
				observe: 'response',
				responseType: 'json',
				headers: {
					'X-App-WebVersion': 'init',
					'X-App-Lang': localStorage.getItem('languageCode'),
				} as any,
			};
			forkJoin([
				this._http.get<BackendApiResponse<any>>(
					`${environment.api.uri}/api/Client/v2/units/${this.unitService.rootUnit.id}/about`,
					requestOptions,
				),
				this._http.get<BackendApiResponse<any>>(`${environment.api.uri}/api/Client/v2/identity`, requestOptions),
				this._http.get<BackendApiResponse<any>>(
					`${environment.api.uri}/Api/Office/${this.licenceHolderService.id}/Identity/Info`,
					requestOptions,
				),
			]).subscribe(
				async ([about, patientIdentity, resourceIdentity]) => {
					this.webVersion = about.headers.get('X-App-WebVersion');

					// Use config that's loaded in new ConfigService via boot above
					const traceableRoute = this.unitService.normalizePathnameToBackTraceableRoute(location.pathname);
					const unitId = this.unitService.backTraceUnitIdFromRoute(traceableRoute);
					universalConfig.currentUnitId = unitId;
					let loadedConfig: NewUnitConfigApiModel = this.configService.__TEMP__loadedConfigsRaw.get(unitId);
					if (loadedConfig == null) {
						loadedConfig = await new Promise<NewUnitConfigApiModel>((resolve) => {
							const interval = setInterval(() => {
								try {
									const config = this.configService.__TEMP__loadedConfigsRaw.get(unitId);
									if (config != null) {
										clearInterval(interval);
										resolve(config);
									}
								} catch {
									// noop
								}
							}, 50);
						});
					}

					this.unitConfigData = {
						...loadedConfig,

						About: about.body.Data,
						WebContentButtons: [],
					};

					this.unitTreeData = result.unitServiceResult;

					if (patientIdentity.body.Data !== null) this.patientIdentityData = patientIdentity.body.Data;

					if (resourceIdentity.body.Data !== null) this.resourceIdentityData = resourceIdentity.body.Data;

					µ.Dict = this.unitConfigData.Translations; // Until this.translateService.get is no longer used

					this.afterDataLoad();

					resolve();
				},
				(error) => {
					console.error(error);
					document.body.innerHTML =
						'<h1 class="title -h1" style="position:absolute;top:40%;left:50%;transform:translateX(-50%);">Unable to load application! Please try again later!</h1>';
				},
			);
		});
	}

	private useGoogleFontPrevention() {
		const head = document.getElementsByTagName('head')[0] as any;
		const insertBefore = head.insertBefore;
		head.insertBefore = (newElement: any, referenceElement: any) => {
			if (newElement.href && newElement.href.indexOf('//fonts.googleapis.com/css?') > -1) {
				return;
			}
			insertBefore.call(head, newElement, referenceElement);
		};
	}

	private afterDataLoad() {
		this.setGlobalDateNames();

		if (this.unitConfigData.Theme !== null) {
			const themeEl = document.createElement('link');
			themeEl.rel = 'stylesheet';
			themeEl.type = 'text/css';
			themeEl.href = `${environment.api.uri}/Api/General/Theme/holder/${this.unitConfigData.LicenceHolder.Id}`;
			document.body.appendChild(themeEl);

			// TODO: Uncomment this when we can actually support this
			// if (this.unitConfigData.Theme.FontUrl != null) {
			// 	const fontEl = document.createElement('link');
			// 	fontEl.rel = 'stylesheet';
			// 	fontEl.type = 'text/css';
			// 	fontEl.href = this.unitConfigData.Theme.FontUrl.toString();
			// 	document.head.appendChild(fontEl);
			// }
		}
	}

	private setGlobalDateNames() {
		µ.Env.i18n = {
			dayNames: [
				this.unitConfigData.Translations['Common.Calendar.DayShort.0'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.1'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.2'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.3'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.4'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.5'],
				this.unitConfigData.Translations['Common.Calendar.DayShort.6'],
				this.unitConfigData.Translations['Common.Calendar.Day.0'],
				this.unitConfigData.Translations['Common.Calendar.Day.1'],
				this.unitConfigData.Translations['Common.Calendar.Day.2'],
				this.unitConfigData.Translations['Common.Calendar.Day.3'],
				this.unitConfigData.Translations['Common.Calendar.Day.4'],
				this.unitConfigData.Translations['Common.Calendar.Day.5'],
				this.unitConfigData.Translations['Common.Calendar.Day.6'],
			],
			monthNames: [
				this.unitConfigData.Translations['Common.Calendar.MonthShort.1'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.2'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.3'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.4'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.5'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.6'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.7'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.8'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.9'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.10'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.11'],
				this.unitConfigData.Translations['Common.Calendar.MonthShort.12'],
				this.unitConfigData.Translations['Common.Calendar.Month.1'],
				this.unitConfigData.Translations['Common.Calendar.Month.2'],
				this.unitConfigData.Translations['Common.Calendar.Month.3'],
				this.unitConfigData.Translations['Common.Calendar.Month.4'],
				this.unitConfigData.Translations['Common.Calendar.Month.5'],
				this.unitConfigData.Translations['Common.Calendar.Month.6'],
				this.unitConfigData.Translations['Common.Calendar.Month.7'],
				this.unitConfigData.Translations['Common.Calendar.Month.8'],
				this.unitConfigData.Translations['Common.Calendar.Month.9'],
				this.unitConfigData.Translations['Common.Calendar.Month.10'],
				this.unitConfigData.Translations['Common.Calendar.Month.11'],
				this.unitConfigData.Translations['Common.Calendar.Month.12'],
			],
		};
	}

	private createRoutes(): void {
		this.router.resetConfig(root());
	}
}
